import { Drawer, ThemeProvider } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getSelectorAttribute, navigationSelectors } from '@vy/app-selectors';
import { vyTheme } from '../../../assets/themes/vyTheme';
import { useActivate } from '../../../hooks/use-activate';
import { NavItemList } from './side-nav-bar-components/NavItemList';
import { SideNavbarFooter } from './side-nav-bar-components/SideNavbarFooter';
import { SideNavbarHeader } from './side-nav-bar-components/SideNavbarHeader';
import { SIDE_NAV_BAR_ROUTES } from './side-nav-bar-types/side-nav-bar-routes';
import { SIDE_NAV_BAR_LABELS } from './side-nav-bar-types/side-nav-bar-labels';
import { useSidebarContext } from '../../../contexts/side-bar-context/hooks/use-side-bar-context';

const HOVER_OPEN_DELAY_MS = 200;

export const SideNavbar: React.FC = () => {
  const location = useLocation();

  const { isOpen, setIsOpen } = useSidebarContext();

  const [isInvoicesMenuOpen, openInvoicesMenu, closeInvoicesMenu] = useActivate(
    location.pathname.startsWith(SIDE_NAV_BAR_ROUTES.Invoices),
  );

  const [isMeteringMenuOpen, openMeteringMenu, closeMeteringMenu] = useActivate(
    location.pathname.startsWith('/usage'),
  );

  const hoverOpenTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const drawerRootRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    clearTimeout(hoverOpenTimerRef.current);
    hoverOpenTimerRef.current = setTimeout(() => setIsOpen(true), HOVER_OPEN_DELAY_MS);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverOpenTimerRef.current);
    setIsOpen(false);
  };

  useEffect(() => () => clearTimeout(hoverOpenTimerRef.current), []);

  // Safety net for the cases where mouseleave never fires and the nav would stay stuck
  // open: a touch/pen tap (no hover model), focus steal (new tab, native dialog,
  // alt-tab), and remote-desktop sessions that drop mouse events. None of these fire
  // during normal mouse use — while the nav is open the cursor is over it, so clicks
  // land inside — so this adds no visible behavior for mouse users. Only armed while open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const closeNav = () => setIsOpen(false);

    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!drawerRootRef.current?.contains(event.target as Node)) {
        closeNav();
      }
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        closeNav();
      }
    };

    document.addEventListener('pointerdown', handlePointerDownOutside);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', closeNav);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', closeNav);
    };
  }, [isOpen, setIsOpen]);

  const handleSubMenuClick = (itemText: string) => {
    if (itemText === SIDE_NAV_BAR_LABELS.Invoices) {
      isInvoicesMenuOpen ? closeInvoicesMenu() : openInvoicesMenu();
    }
    if (itemText === SIDE_NAV_BAR_LABELS.DataMetering) {
      isMeteringMenuOpen ? closeMeteringMenu() : openMeteringMenu();
    }
  };

  const isSubmenuOpen = (itemText: string) => {
    if (!isOpen) {
      return false;
    }
    if (itemText === SIDE_NAV_BAR_LABELS.Invoices) {
      return isInvoicesMenuOpen;
    }
    if (itemText === SIDE_NAV_BAR_LABELS.DataMetering) {
      return isMeteringMenuOpen;
    }

    return false;
  };

  return (
    <ThemeProvider theme={vyTheme}>
      <Drawer
        ref={drawerRootRef}
        variant="permanent"
        open={isOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...getSelectorAttribute(navigationSelectors.sidebar)}
        sx={{
          position: 'relative',
          width: '96px',
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            position: 'absolute',
            top: 0,
            left: 0,
            whiteSpace: 'nowrap',
            zIndex: 9999,
            width: isOpen ? '248px' : '96px',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            overflowX: 'hidden',
            bgcolor: 'primary.dark',
            backgroundImage: 'linear-gradient(180deg, #0F1E4B 0%, #0A1232 100%)',
            color: '#fff',
            borderRadius: 0,
            height: '100vh',
            borderRight: 0,
          },
        }}
      >
        <SideNavbarHeader isDrawerOpen={isOpen} />

        <NavItemList
          handleSubMenuClick={handleSubMenuClick}
          isSubmenuOpen={isSubmenuOpen}
          isDrawerOpen={isOpen}
        />

        <SideNavbarFooter />
      </Drawer>
    </ThemeProvider>
  );
};
